// hooks/useWallet.js
// ─────────────────────────────────────────────────────────────
// CHANGES:
// Backend now returns fiatBalance, fiatCurrency, fiatSymbol
// alongside nairaBalance. Hook exposes all of them so pages
// can display the correct currency symbol regardless of country.
// nairaBalance kept for backwards compat — it's the same number.
// ─────────────────────────────────────────────────────────────
import { useState, useEffect, useRef, useCallback } from "react";
import { getUserWallet } from "../api/apiServices";

const SYMBOL_COLORS = {
  BTC:  "#F7931A", ETH:  "#627EEA", USDT: "#26A17B",
  BNB:  "#F3BA2F", SOL:  "#9945FF", XRP:  "#00AAE4",
  DOGE: "#C2A633", ADA:  "#0033AD", TON:  "#0088CC",
  NOT:  "#222222",
};
export const assetColor = (symbol) => SYMBOL_COLORS[symbol] || "#64748B";

const SYMBOL_ICONS = {
  BTC: "ri-bitcoin-line", ETH: "ri-eth-line",
  USDT: "ri-money-dollar-circle-line", BNB: "ri-coin-line",
  SOL: "ri-star-line",
};
export const assetIcon = (symbol) => SYMBOL_ICONS[symbol] || "ri-currency-line";

export function useWallet({
  enabled      = true,
  priceMap     = {},
  pollInterval = 30_000,
} = {}) {
  const [nairaBalance,    setNairaBalance]    = useState(0);
  const [fiatBalance,     setFiatBalance]     = useState(0);
  const [fiatCurrency,    setFiatCurrency]    = useState("NGN");
  const [fiatSymbol,      setFiatSymbol]      = useState("₦");
  const [referralBalance, setReferralBalance] = useState(0);
  const [rawAssets,       setRawAssets]       = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);
  const initialised = useRef(false);
  const timerRef    = useRef(null);

  const fetch_ = useCallback(async () => {
    if (!enabled) { setLoading(false); return; }
    if (!initialised.current) setLoading(true);
    setError(null);
    try {
      const res = await getUserWallet();
      const d   = res?.data || res;

      const bal = Number(d.fiatBalance ?? d.nairaBalance ?? 0);
      setNairaBalance(bal);
      setFiatBalance(bal);
      setFiatCurrency(d.fiatCurrency || "NGN");
      setFiatSymbol(d.fiatSymbol    || "₦");
      setReferralBalance(Number(d.referralBalance || 0));

      if (d.assets) {
        setRawAssets(d.assets);
      } else if (d.wallets) {
        const grouped = {};
        (d.wallets || []).forEach(w => {
          if (!grouped[w.currency]) {
            grouped[w.currency] = { currency: w.currency, totalBalance: 0, networks: [] };
          }
          grouped[w.currency].totalBalance += Number(w.balance || 0);
          grouped[w.currency].networks.push({
            network: w.network, address: w.address,
            balance: Number(w.balance || 0), contractAddress: w.contractAddress || "",
          });
        });
        setRawAssets(Object.values(grouped));
      }
    } catch (err) {
      setError(err?.message || "Failed to load wallet");
    } finally {
      setLoading(false);
      initialised.current = true;
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) { setLoading(false); return; }
    fetch_();
    if (pollInterval > 0) {
      timerRef.current = setInterval(fetch_, pollInterval);
    }
    return () => clearInterval(timerRef.current);
  }, [fetch_, enabled, pollInterval]);

  const holdings = rawAssets
    .map(asset => {
      const price    = priceMap[asset.currency];
      const priceNGN = price?.priceNgn || 0;
      return {
        symbol:       asset.currency,
        name:         price?.name      || asset.currency,
        totalBalance: asset.totalBalance || 0,
        valueNGN:     (asset.totalBalance || 0) * priceNGN,
        change24h:    price?.change24h  || 0,
        image:        price?.image      || "",
        color:        assetColor(asset.currency),
        icon:         assetIcon(asset.currency),
        networks:     asset.networks    || [],
      };
    })
    .filter(h => h.totalBalance > 0)
    .sort((a, b) => b.valueNGN - a.valueNGN);

  const cryptoTotal    = holdings.reduce((s, h) => s + h.valueNGN, 0);
  const totalPortfolio = fiatBalance + cryptoTotal;

  return {
    nairaBalance,     // kept for backwards compat
    fiatBalance,      // currency-neutral — same number, clearer name
    fiatCurrency,     // e.g. "NGN", "GHS", "KES"
    fiatSymbol,       // e.g. "₦", "₵", "KSh"
    referralBalance,
    assets:          rawAssets,
    holdings,
    totalPortfolio,
    loading,
    error,
    refresh: fetch_,
  };
}
